import React, { useEffect, useState } from "react";
import InlineTextInput from "./common-components/InlineTextInput";
import BreadCrumbForm from "./common-components/BreadCrumbForm";
import FormHeading from "./common-components/FormHeading";
import CheckboxGroupInput from "./common-components/CheckboxInput";
import { inductionQuestions } from "./form-data/InductionFeedback-data";
import { GoDotFill } from "react-icons/go";
import InlineDateField from "./common-components/InlineDateField";
import useApi from "../../core/customhook/useApi";
import { validateInductionForm } from "./validation/form-validations/Induction-validation";
import Swal from "sweetalert2";
import { fetch_induction_feedback, create_induction_feedback, get_prefilled_details } from "../../environment";
import { formattedDate } from "../../core/common/formattedDate";
import dayjs from "dayjs";
import ViewSignature from "../../core/common/ViewSignature";
import ESignaturePad from "../../core/common/ESignaturePad";
import { toast } from "react-toastify";
import Loading from "../../core/common/custom-loading/Loading";

const sectionConfig = [
  { key: "inductionSession", title: "INDUCTION SESSION" },
  { key: "inductionInDepartment", title: "INDUCTION IN YOUR DEPARTMENT" },
  { key: "others", title: "OTHERS" },
];

export type InductionProps = {
  name: string;
  priorEmployer: string;
  dateOfJoining: string;
  departmentName: string;

  inductionRatingOverall: string;
  inductionHelpedUnderstandJob: string;
  introducedToColleagues: string;
  inductionHelpedMakeFriends: string;
  topicsRelevantToWork: string;
  managerMetOnFirstDay: string;
  warmWelcomeByColleagues: string;
  knowWhoToApproach: string;
  jobResponsibilitiesExplained: string;
  checklistUseful: string;
  receivedOfficeTour: string;
  policiesExplained: string;
  payrollPoliciesCovered: string;
  receivedLiteratureCopy: string;
  paperworkAvailable: string;
  signatureDate: string;
  signature: File | null;
  signature_url?: string;
};

const initialFormData = {
  name: "",
  priorEmployer: "",
  dateOfJoining: "",
  departmentName: "",

  inductionRatingOverall: "",
  inductionHelpedUnderstandJob: "",
  introducedToColleagues: "",
  inductionHelpedMakeFriends: "",
  topicsRelevantToWork: "",
  managerMetOnFirstDay: "",
  warmWelcomeByColleagues: "",
  knowWhoToApproach: "",
  jobResponsibilitiesExplained: "",
  checklistUseful: "",
  receivedOfficeTour: "",
  policiesExplained: "",
  payrollPoliciesCovered: "",
  receivedLiteratureCopy: "",
  paperworkAvailable: "",
  signatureDate: "",
  signature: null,
  signature_url: ""
};

const InductionFeedbackForm = () => {
  const DotIcon = GoDotFill as unknown as React.FC;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<InductionProps>(initialFormData);
  const [isAlreadySaved, setIsAlreadySaved] = useState<boolean>(false)


  const { callApi: getEmpDetailsApi, loading: empDetailsLoading } = useApi(
    get_prefilled_details,
    {
      method: "GET",
      autoCall: false,
    },
  );
  const {
    callApi: getSavedDataApi,
    loading: loading_get,
  } = useApi(fetch_induction_feedback, {
    method: "GET",
    autoCall: false,
  });
  const {
    callApi: saveInductionApi,
    loading,
    error,
  } = useApi(create_induction_feedback, {
    method: "POST",
    autoCall: false,
  });

  const handleError = (name: keyof InductionProps) =>
    setErrors((prev: any) => {
      const { [name]: _, ...rest } = prev;
      return rest;
    });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: InductionProps) => ({
      ...prev,
      [name]: value,
    }));
    handleError(name as keyof InductionProps);
  };

  const handleDateChange = (name: string, value: string) => {
    const [day, month, year] = value?.split("-");
    const formatted = `${year}-${month}-${day}`;
    setFormData((prev) => ({ ...prev, [name]: formatted }));
    handleError(name as keyof InductionProps);
  };


  const handleSignatureChange = (
    field: keyof InductionProps,
    file: File | null,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: file }));
    handleError(field);
  };

  const handleFieldDataChange = (name: string, value: string) => {
    setFormData((prev: InductionProps) => ({
      ...prev,
      [name]: prev[name as keyof InductionProps] == value ? "" : value,
    }));
    handleError(name as keyof InductionProps);
  };

  // ==== fetch prefilled employee data =====
  const fetchEmpData = async () => {
    try {
      const res = await getEmpDetailsApi();
      if (res?.success) {
        const resData = res?.data || {};
        setFormData((prev) => ({
          ...prev,
          employee_id: resData?.employee_id ?? "",
          name: resData?.employee_name ?? "",
          dateOfJoining: resData?.join_date ?? "",
          departmentName: resData?.department ?? "",
          signatureDate: dayjs().format("YYYY-MM-DD")
        }));
      }
    } catch (error) {
      console.error("failed to fetch:", error);
    }
  };

  // fetch saved form data
  const fetchSavedData = async () => {
    try {
      const res = await getSavedDataApi();
      if (res?.success) {
        const data = res?.data || null;
        if (!data) {
          fetchEmpData();
          return;
        };

        setIsAlreadySaved(true);
        setFormData((prev) => ({
          ...prev,
          name: data?.name ?? "",
          priorEmployer: data?.prior_employer ?? "",
          dateOfJoining: data?.date_of_joining ?? "",
          departmentName: data?.department ?? "",

          inductionRatingOverall: data?.induction_rating ?? "",
          inductionHelpedUnderstandJob:
            data?.induction_helped_understand_job ?? "",
          introducedToColleagues: data?.introduced_to_colleagues ?? "",
          inductionHelpedMakeFriends:
            data?.induction_helped_make_friends ?? "",
          topicsRelevantToWork: data?.topics_relevant_to_work ?? "",
          managerMetOnFirstDay: data?.manager_met_on_first_day ?? "",
          warmWelcomeByColleagues: data?.received_warm_welcome ?? "",
          knowWhoToApproach: data?.know_who_to_approach ?? "",
          jobResponsibilitiesExplained:
            data?.job_responsibilities_explained ?? "",
          checklistUseful: data?.checklist_useful ?? "",
          receivedOfficeTour: data?.received_tour_by_qualified ?? "",
          policiesExplained: data?.office_policies_explained ?? "",
          payrollPoliciesCovered: data?.payroll_covered_first_day ?? "",
          receivedLiteratureCopy: data?.received_relevant_literature ?? "",
          paperworkAvailable: data?.paperwork_forms_available ?? "",

          signatureDate: data?.signature_date ?? "",
          signature_url: data?.signature_path ?? ""
        }))
      }
    } catch (error) {
      console.error("failed to fetch:", error);
    }
  };
  useEffect(() => {
    fetchSavedData()
  }, [])

  // -==== handleSubmit ======
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { errors, firstError } = validateInductionForm(formData);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      toast.error("Please fill all required fields")
      return;
    }

    const payload = {
      name: formData?.name ?? "",
      prior_employer: formData?.priorEmployer ?? "",
      // date_of_joining: formData?.dateOfJoining ?? "",
      date_of_joining: dayjs(formData?.dateOfJoining, "DD/MM/YYYY").format("YYYY-MM-DD") ?? "",
      department: formData?.departmentName ?? "",

      induction_rating: formData?.inductionRatingOverall ?? "",
      induction_helped_understand_job:
        formData?.inductionHelpedUnderstandJob ?? "",
      introduced_to_colleagues: formData?.introducedToColleagues ?? "",
      induction_helped_make_friends:
        formData?.inductionHelpedMakeFriends ?? "",
      topics_relevant_to_work: formData?.topicsRelevantToWork ?? "",
      manager_met_on_first_day: formData?.managerMetOnFirstDay ?? "",
      received_warm_welcome: formData?.warmWelcomeByColleagues ?? "",
      know_who_to_approach: formData?.knowWhoToApproach ?? "",
      job_responsibilities_explained:
        formData?.jobResponsibilitiesExplained ?? "",
      checklist_useful: formData?.checklistUseful ?? "",
      received_tour_by_qualified: formData?.receivedOfficeTour ?? "",
      office_policies_explained: formData?.policiesExplained ?? "",
      payroll_covered_first_day: formData?.payrollPoliciesCovered ?? "",
      received_relevant_literature:
        formData?.receivedLiteratureCopy ?? "",
      paperwork_forms_available: formData?.paperworkAvailable ?? "",

      signature_date: formData?.signatureDate ?? "",
      signature: formData?.signature ?? null,
    };


    const formDataPayload = new FormData()

    Object.entries(payload).forEach(([key, value]) => {
      if (key === "signature" && value) {
        formDataPayload.append(key, value as File);
      } else if (value !== null && value !== undefined) {
        formDataPayload.append(key, String(value));
      }
    });

    try {
      const response = await saveInductionApi(formDataPayload);
      // "Submitted!",
      // console.log(response)
      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Submitted!",
          text: "Form submitted successfully",
          confirmButtonText: "OK",
          confirmButtonColor: "#fd7e14",
        });
        fetchSavedData();
        // setFormData(initialFormData);
      }
    } catch (error: any) {
      console.error("error message", error.response);
      const errorMsg = error.response?.data?.message ?? null;
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: errorMsg ?? "Failed to submit form data.",
        confirmButtonText: "OK",
        confirmButtonColor: "#fd7e14",
      });
    }
  };

  // console.log(formData);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <BreadCrumbForm
          title="Induction Assessment Form"
          linkTitle="Induction Assessment Form"
        />

        {/* Form content */}
        {(loading_get || empDetailsLoading) && <div className="card p-3"><Loading /></div>}
        {!loading_get && !empDetailsLoading && <div className="card shadow-sm mt-4 form-card-container">
          <div className="card-body p-4 p-md-5">
            <form onSubmit={handleSubmit}>
              {/* Title */}
              <FormHeading title="Induction Feedback Form" />

              {/* Basic Info */}
              <div>
                <InlineTextInput
                  key={"name"}
                  label="Employee Name:"
                  name="name"
                  value={formData?.name}
                  onChange={handleChange}
                  errors={errors}
                  classStyle="text-capitalize"
                  disabled
                />
                <InlineTextInput
                  key={"priorEmployer"}
                  // label="Prior to joining Team1 Consulting, you were employed with:"
                  label="Previous Employer:"
                  name="priorEmployer"
                  value={formData?.priorEmployer}
                  onChange={handleChange}
                  errors={errors}
                  classStyle="text-capitalize"
                  disabled={isAlreadySaved || loading}
                />
                <InlineTextInput
                  label="Date of Joining:"
                  name="dateOfJoining"
                  value={formData?.dateOfJoining ?? ""}
                  onChange={handleChange}
                  errors={errors}
                  disabled
                />
                {/* <InlineDateField
                  label="Date of Joining:"
                  name="dateOfJoining"
                  value={formData?.dateOfJoining ?? ""}
                  onChange={handleDateChange}
                  errors={errors}
                /> */}
                <InlineTextInput
                  label="Department:"
                  name="departmentName"
                  value={formData?.departmentName}
                  onChange={handleChange}
                  errors={errors}
                  disabled
                />
              </div>
              <hr />

              <div className="mt-4">
                {sectionConfig?.map(({ key, title }, index) => (
                  <div key={key || index + 1} className="mb-4 ">
                    <div
                      className="mb-2 d-flex align-items-center gap-2 "
                    >
                      <span className="fs-8 mb-3 text-dark">
                        <DotIcon />
                      </span>
                      <p
                        className="fw-bold mb-3"
                        style={{ fontSize: "16px", color: "#111827" }}
                      >
                        {title}
                      </p>
                    </div>
                    {inductionQuestions[
                      key as keyof typeof inductionQuestions
                    ]?.map((q) => (
                      <CheckboxGroupInput
                        key={q.name}
                        label={q.label}
                        name={q.name}
                        options={q.options}
                        value={
                          formData[q.name as keyof Omit<InductionProps, "signature">] ?? ""
                        }
                        onChange={handleFieldDataChange}
                        errors={errors}
                        isDisabled={isAlreadySaved}
                      />
                    ))}
                  </div>
                ))}
                <hr />

                {/* Signature */}
                <div className="mt-5 pb-5">
                  <div className="mb-3">
                    {/* {!isAlreadySaved ? <ViewSignature fileSource={typeof formData?.signature == 'string' ? formData?.signature : ""} /> : <> */}
                    <label htmlFor="signature" className="form-label fw-semibold">
                      Signature of Employee:
                    </label>
                    {isAlreadySaved ? <ViewSignature fileSource={formData?.signature_url ?? ""} /> :
                      <ESignaturePad
                        field="signature"
                        onChange={handleSignatureChange}
                        existingSignatureUrl={
                          typeof formData.signature === "string"
                            ? formData.signature // URL string from API (edit mode)
                            : null
                        }
                        error={errors.signature}
                      // isDisabled={submitLoading}
                      />
                    }
                  </div>
                  <InlineDateField
                    label="Date:"
                    name="signatureDate"
                    value={formData?.signatureDate ?? ""}
                    onChange={handleDateChange}
                    errors={errors}
                    disabled
                  />
                </div>
              </div>
              <div className="text-end">
                <button
                  type="submit"
                  // disabled={Object.keys(errors)?.length > 0 || loading}
                  disabled={loading || isAlreadySaved}
                  className="btn btn-primary"
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
          {/* Form content */}
        </div>}

        <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
          <p className="mb-0"> © {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default InductionFeedbackForm;
